import React, { Component, ReactNode } from 'react';
import AccountProfileComponent from './AccountProfileComponent';
import AccountSecurityComponent from './AccountSecurityComponent';
import AccountQrCodeComponent from './AccountQrCodeComponent';
import Breadcrumb from '../Breadcrumb';
import AccountPreferencesComponent from './AccountPreferencesComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import { User } from '../../domain/rbac/RbacDomains';
import CollectionNames from '../../constant/CollectionNames';
import { RootState, store } from '../../redux/store';
import { clearPage } from '../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import CustomLoading from '../custom/CustomLoading';
import FolderService from '../../service/FolderService';
import ComponentName from '../../constant/ComponentName';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { initialPagePath, PageState } from '../../redux/reducers/PageReducer';
import Subscription = subscribe.Subscription;
import Computation = subscribe.Computation;
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Tracker = subscribe.Tracker;

class AccountContainerComponent extends Component<Props, State> {
  public state: State = new State();

  private userSubscription: Subscription | undefined;
  private computation: Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const { loading, user } = this.state;
    if (loading || user == undefined) {
      return <CustomLoading />;
    }
    return (
      <div className='w-full h-full flex flex-col overflow-scroll bg-[#ECF1F5] opacity-[10]'>
        <Breadcrumb />
        <div className='h-full w-full flex flex-row p-4 gap-3'>
          <AccountProfileComponent user={user} />
          <AccountSecurityComponent />
          <div className='h-full w-full flex flex-col gap-y-3'>
            <AccountPreferencesComponent user={user} />
            <AccountQrCodeComponent />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      router: { location },
    } = this.props;
    const page: PageState = store.getState().page;
    const initial = initialPagePath[ComponentName.ACCOUNT];
    if (page.pagePath.length == 0 || page.pagePath[0].title != initial?.title) {
      await FolderService.calculate('WITHOUT_FOLDER', ComponentName.ACCOUNT, location.pathname, store.getState().page);
    }
    this.startTracker();
  }

  public componentWillUnmount() {
    const { clearPage } = this.props;
    clearPage();
    this.stopTracker();
  }

  private startTracker = () => {
    const { user } = this.props;
    const userQuery = QueryBuilder.select().from(CollectionNames.USERS).where(eq('id', user.username)).build();
    this.userSubscription = subscribe.Formica.subscribe(CollectionNames.USERS, userQuery);
    this.computation = Tracker.autorun(this.tracker, [this.userSubscription]);
  };

  private tracker = async () => {
    if (this.userSubscription?.ready()) {
      const userQuery = QueryBuilder.select().from(CollectionNames.USERS).where(eq('id', this.props.user.username)).build();
      const user = await this.repository.find<User>(userQuery);
      this.setState({ user, loading: false });
    }
  };

  private stopTracker = () => {
    this.userSubscription?.stop();
    this.computation?.stop();
  };
}

class State {
  user: User | undefined;
  loading: boolean = true;
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const mapDispatchToProps = { clearPage };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(AccountContainerComponent));
